import { z } from 'zod'

import { getUtcDate, isTimeAfterOrEqual } from '../helpers'

export type ITimeQuote = z.infer<typeof timeQuoteSchema>

export const timeQuoteSchema = z
  .object({
    from: z.string().nullish(),
    to: z.string().nullish(),
    type: z.enum(['by', 'at', 'between', '24', 'tbc', 'asap']).default('between').optional(),
  })
  .refine(
    time => {
      switch (time.type) {
        case 'between':
          return !!time.from && !!time.to
        default:
          return true
      }
    },
    {
      message: 'Both start and end times are required',
    },
  )
  .refine(
    time => {
      if (time.type === 'at' && !time.from) return false
      if (time.type === 'by' && !time.to) return false
      return true
    },
    {
      message: 'Please select a time',
    },
  )
  .refine(
    time => {
      if (time.type === 'between') {
        if (!time.from || !time.to) return false

        return isTimeAfterOrEqual(time.from, time.to)
      }

      return true
    },
    {
      message: 'Start time must be before end time',
    },
  )

export const dateTimeQuote = z.object({
  date: z.string(),
  time: timeQuoteSchema,
})

export type IDateTimeQuote = z.infer<typeof dateTimeQuote>

export const placeQuote = z.object({
  address_book: z
    .object({
      id: z.number().optional(),
      name: z
        .string()
        .nullish()
        .transform(name => {
          if (name === null) return undefined

          return name
        }),
    })
    .nullish(),
  address_id: z.number().optional(),
  city: z.string().nullish(),
  country: z.string().optional(),
  country_code: z.string().optional(),
  lat: z.coerce.string().optional(),
  lng: z.coerce.string().optional(),
  name: z
    .string()
    .nullish()
    .transform(name => name ?? undefined),
  place_id: z.string().optional(),
  place_name: z.string().optional(),
  postcode: z
    .string()
    .nullish()
    .transform(postcode => String(postcode)),
})
export type IPlaceQuote = z.infer<typeof placeQuote>

export const travelPointQuoteSchema = z.object({
  date: z.coerce
    .date()
    .nullish()
    .transform(date => (date ? getUtcDate(date).toISOString() : undefined)),
  id: z.number().optional(),
  order: z.number(),
  place: placeQuote.refine(
    place => place.city || place.name || place.place_id || place.place_name,
    { message: 'Please select a location' },
  ),
  reference_no: z.string().nullish(),
  time: timeQuoteSchema,
  type: z.enum(['collection', 'delivery']),
})
export type ITravelPointQuote = z.infer<typeof travelPointQuoteSchema>

const addressIdSchema = z.object({
  addressId: z.number().optional(),
})

export const travelPointQuoteWithAddressIdSchema = travelPointQuoteSchema.merge(addressIdSchema)

export interface IDrop {
  date: string | undefined
  id: number
  order: number
  place: IPlace
  reference_no: string | undefined
  time: ILoadTime
  type: string
}

export interface IPlace {
  address_book?: {
    name: string
  }
  city: string
  country: string
  country_code: string
  id: number
  lat: string
  lng: string
  name?: string
  place_id: string
  place_name: string
  point: string
  postcode: string
}

export interface IQSDrops {
  destination: {
    city?: string
    country: string
    formattedAddress: string
    lat: string
    lng: string
    place_id: string
    place_name: string
    postcode?: string
  }
  host?: string
  load_date: string
  load_type: {
    id: number
    slug: string
    title: string
  }
  new_user: string
  origin: {
    city?: string
    country: string
    formattedAddress: string
    lat: string
    lng: string
    place_id: string
    place_name: string
    postcode?: string
  }
}

export type TravelPointQuoteWithAddressId = z.infer<typeof travelPointQuoteWithAddressIdSchema>

interface ILoadTime {
  from: string
  to: string
  type: string
}

export const newQuoteSchema = z.object({
  action: z.string().optional(),
  company_id: z.number(),
  drops: travelPointQuoteSchema.array().transform(drops =>
    drops.map((drop, index) => {
      return {
        ...drop,
        order: index,
      } satisfies ITravelPointQuote
    }),
  ),
  goods: z.string().array().optional(),
  goods_description: z.string().optional(),
  id: z.number().optional(),
  load_type_id: z.coerce.number().optional(),
  notes: z.string(),
  packaging_type_id: z.coerce.number().optional(),
  pallets: z.coerce.number().default(0),
  requirements: z.object({
    adr: z.boolean(),
    dvs: z.boolean(),
    waste_license: z.boolean(),
  }),
  temperature: z.number().optional(),
  user_id: z.number(),
  weight: z.string(),
})

export type INewQuoteForm = z.infer<typeof newQuoteSchema>

export const editQuoteSchema = z.object({
  company_id: z.number(),
  drops: travelPointQuoteSchema.array().superRefine((drops, ctx) => {
    const res = drops.every((drop, index) => {
      // Always allow the first drop
      if (index === 0) return true

      // If either the current drop or the previous drop doesn't have a date, it's ok
      if (!drop.date || !drops[index - 1].date) return true

      // If both drops have dates, check if the current drop's date is after the previous drop's date
      return new Date(drop.date) >= new Date(drops[index - 1].date!)
    })

    if (!res) {
      ctx.addIssue({
        code: 'custom',
        message: 'Dates of the drops must be in order',
      })
    }
  }),
  goods: z.string().array().optional(),
  id: z.number().optional(),
  load_type_id: z.coerce.number().optional(),
  notes: z.string().optional(),
  packaging_type_id: z.number().optional(),
  pallets: z.number().optional(),
  requirements: z
    .object({
      adr: z.boolean(),
      dvs: z.boolean(),
      waste_license: z.boolean(),
    })
    .optional(),
  temperature: z.number().optional(),
  user_id: z.number(),
  weight: z.string().optional(),
})

export type IEditQuoteForm = z.infer<typeof editQuoteSchema>

export const rebookSchema = z.object({
  customer_rate: z.number(),
  drops: z.array(
    z.object({
      date: z.coerce.date().transform(date => getUtcDate(date).toISOString()),
      id: z.number(),
      time: timeQuoteSchema,
    }),
  ),
  preferred_rate: z.object({
    currency: z.string(),
    rate: z.number(),
  }),
})

export type IRebookQuoteForm = z.infer<typeof rebookSchema>

export const newLoadSchema = newQuoteSchema.merge(
  z.object({
    rate: z.coerce.number(),
    status: z.string(), //todo change to enum
  }),
)

export type INewLoadForm = z.infer<typeof newLoadSchema>
